import { ChangeDetectorRef, inject, Injectable } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';

import type { InterviewResult } from '@core/interview-results-types';
import type { Placement } from '@core/placements-types';
import type { EditorJSONState } from '@core/prosemirror-types';
import type { QuickEvaluationForm, QuickEvaluationFormValue } from '@recruitee/evaluations-types';

import { EvaluationFormSubmissionService } from '../evaluation-form-submission.service';

@Injectable()
export class QuickEvaluationFormService {
  private readonly fb = inject(FormBuilder);
  private readonly formSubmissionService = inject(EvaluationFormSubmissionService);
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly form: QuickEvaluationForm = this.fb.group({
    offerId: new FormControl<number | null>(null),
    stageId: new FormControl<number | null>(null),
    score: new FormControl<number | null>(null, Validators.required),
    ratingNote: new FormControl<string | null>(null),
    ratingNoteJson: new FormControl<EditorJSONState | null>(null),
  });

  public init(offerId: number | undefined, placements: Placement[]): void {
    if (!offerId) return;

    const placement = placements.find(
      placement => placement.offerId === offerId || placement.talentPoolId === offerId,
    );

    this.form.patchValue({
      offerId,
      stageId: placement?.stageId ?? null,
    });
  }

  public patchFromResult(result: InterviewResult): void {
    this.form.patchValue({
      offerId: result.offerId ?? result.talentPoolId,
      stageId: result.stageId != null ? Number(result.stageId) : null,
      score: result.score ?? null,
      ratingNote: result.ratingNote,
      ratingNoteJson: result.ratingNoteJson ?? null,
    });
  }

  public restoreDraft(value: QuickEvaluationFormValue): void {
    this.form.patchValue(value);
    this.form.markAsDirty();
    this.form.updateValueAndValidity();
    this.cdRef.detectChanges();
  }

  public clear(): void {
    this.form.patchValue({ score: null, ratingNote: null, ratingNoteJson: null });
    this.form.markAsUntouched();
    this.formSubmissionService.reset();
    this.form.updateValueAndValidity();
    this.cdRef.detectChanges();
  }

  public reset(): void {
    this.form.reset();
  }
}
